"use client";

import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { useLocation } from "react-router-dom";
import { Navigation } from "./navigation";
import { Footer } from "./footer";
import { defaultLocale, type Locale } from "@/lib/i18n";

interface LayoutProps {
  children: React.ReactNode;
  locale?: Locale;
}

function useThemeColor() {
  useEffect(() => {
    const updateThemeColor = () => {
      const isDark = document.documentElement.classList.contains("dark");
      const color = isDark ? "#020817" : "#ffffff";
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        meta.setAttribute("content", color);
      }
    };

    updateThemeColor();

    const observer = new MutationObserver(updateThemeColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);
}

export function Layout({ children, locale = defaultLocale }: LayoutProps) {
  useThemeColor();
  const { pathname } = useLocation();
  // Kiosk-Ansicht ohne Navigation und Footer
  const isChromeless = /^\/(de|en|fr|it)?\/?rundgang(\/|$)/.test(pathname);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="althaus-theme"
    >
      <div className="min-h-screen flex flex-col bg-white dark:bg-background">
        {!isChromeless && <Navigation locale={locale} />}
        <main className="flex-1">
          {children}
        </main>
        {!isChromeless && <Footer locale={locale} />}
      </div>
    </ThemeProvider>
  );
}